import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  IconButton,
  Link as MuiLink,
  Paper,
  Typography,
} from "@mui/material";
import { lighten, Theme } from "@mui/material/styles";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { usePortfolio } from "../../context/PortfolioContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import { percentage, percentageNoDecimals } from "../../shared/percentage.ts";
import { ValueByClaimType } from "../../shared/PortfolioTypes.ts";
import { PartyValueInCase } from "../../shared/ValueStatistics.ts";
import {
  gainFill,
  NO_VALUE,
  signedTone,
  TABULAR_NUMERALS,
  toneColors,
} from "../general/dataAtoms/shared.ts";
import { SectionLabel } from "../general/SectionLabel.tsx";
import { SegmentedControl } from "../general/SegmentedControl.tsx";
import { SplitBar } from "../general/SplitBar.tsx";
import { ToggleProgressBar } from "../general/ToggleProgressBar.tsx";
import { TonalBadge } from "../general/TonalBadge.tsx";
import { ClaimTypeAverages } from "./ClaimTypeAverages.tsx";
import { ClaimTypeBreakdown } from "./ClaimTypeBreakdown.tsx";
import { earnedShare } from "../../shared/earnedShare.ts";
import { HistogramChart } from "./HistogramChart.tsx";
import { InteractiveBar, LinkedBarChart } from "./LinkedBarChart.tsx";
import { card, cardRow, SectionHeading } from "./PortfolioSection.tsx";
import { WithoutFeeStructure } from "./WithoutFeeStructure.tsx";

/** What of a revenue or a profit is looked at. */
type Variant = "incurred" | "estimated" | "total";
const VARIANTS: readonly Variant[] = ["incurred", "estimated", "total"];

/** What the progress of the cases is measured by. */
type ProgressType = "cost" | "margin";

/** The fill of a part of a divided bar, and the colour of text on it. */
type PartColors = { color: string; ink: string };

/**
 * The colour a result is written in: that of a gain or of a loss, and the ink
 * of the page where it is neither.
 */
const resultInk = (theme: Theme, result: number): string | undefined =>
  result === 0 ? undefined : toneColors(theme)[signedTone(result)];

/**
 * How far what is earned and what is estimated of the revenue are lightened
 * from the primary colour: what is earned is the firmer of the two.
 */
const EARNED_LIGHTEN = 0.2;
const ESTIMATED_LIGHTEN = 0.6;

/** The number of ranges the progress of the active cases is counted in. */
const PROGRESS_BINS = 10;

/**
 * The chart of the cases is no narrower than this, in px, so that the bar of
 * a case stays wide enough to select; a narrower page scrolls the chart.
 */
const CASE_CHART_MIN_WIDTH = 760;

const figureLabel = {
  display: "flex",
  alignItems: "center",
  gap: 0.875,
  fontWeight: 500,
  color: "text.secondary",
} as const;

const figureValue = {
  fontWeight: 700,
  letterSpacing: "-.015em",
  lineHeight: 1.25,
  ...TABULAR_NUMERALS,
} as const;

/** The head of a card: what it shows, and a choice of what of it is shown. */
const CardHead = ({
  title,
  control,
}: {
  title: string;
  control?: ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 1.5,
    }}
  >
    <SectionLabel component="h3">{title}</SectionLabel>
    {control}
  </Box>
);

/**
 * A total with what of it is earned and what is estimated: the three as
 * figures, and the two parts as shares of one bar.
 */
const EarnedAndEstimatedCard = ({
  title,
  earned,
  estimated,
  colors,
  isResult = false,
}: {
  title: string;
  earned: number;
  estimated: number;
  colors: { earned: PartColors; estimated: PartColors };
  // a result is a gain or a loss, and its total takes the colour of that
  isResult?: boolean;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "LawFirmPortfolio" });
  const total = earned + estimated;

  // a loss stands out wherever it is; a gain only as the total of a result
  const inkOf = (amount: number, isToned = false) =>
    isToned || amount < 0 ? resultInk(theme, amount) : undefined;
  const figure = (
    label: string,
    amount: number,
    color: string | undefined,
    swatch?: string,
  ) => (
    <div>
      <Typography variant="caption" sx={figureLabel}>
        {swatch && (
          <Box
            component="span"
            sx={{
              flex: "none",
              width: 10,
              height: 10,
              borderRadius: 0.75,
              bgcolor: swatch,
            }}
          />
        )}
        {label}
      </Typography>
      <Typography variant="h6" component="p" sx={{ ...figureValue, color }}>
        {currency.format(amount)}
      </Typography>
    </div>
  );

  const share = earnedShare(earned, estimated);
  // whole percentages that add up to 100
  const earnedPercent = share === undefined ? 0 : Math.round(share * 100);
  const parts =
    share === undefined
      ? []
      : [
          {
            key: "incurred",
            label: t("variant.incurred"),
            share,
            shareText: percentageNoDecimals.format(earnedPercent / 100),
            ...colors.earned,
          },
          {
            key: "estimated",
            label: t("variant.estimated"),
            share: 1 - share,
            shareText: percentageNoDecimals.format((100 - earnedPercent) / 100),
            ...colors.estimated,
          },
        ].filter((part) => part.share > 0);

  return (
    <Paper component="section" variant="outlined" sx={card}>
      <SectionLabel component="h3">{title}</SectionLabel>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          columnGap: 3.25,
          rowGap: 2,
          mt: 1.75,
        }}
      >
        {figure(t("variant.total"), total, inkOf(total, isResult))}
        {figure(
          t("variant.incurred"),
          earned,
          inkOf(earned),
          colors.earned.color,
        )}
        {figure(
          t("variant.estimated"),
          estimated,
          inkOf(estimated),
          colors.estimated.color,
        )}
      </Box>
      {/* where neither part is a gain there is nothing to divide */}
      {parts.length > 0 && (
        <Box sx={{ mt: 1.75 }}>
          <SplitBar parts={parts} />
        </Box>
      )}
    </Paper>
  );
};

/**
 * What the cases with each set of claim types bring, of a revenue or a profit:
 * beside one another the amounts with their shares of the whole, and what a
 * case brings on average and at either end. Each card chooses on its own what
 * of it is shown.
 */
const ByClaimType = ({
  kind,
  values,
  sort,
}: {
  kind: "revenue" | "margin";
  values: { [variant in Variant]: ValueByClaimType[] };
  /** The order of the rows in the list of the amounts, by their amounts. */
  sort: (a: { value: number }, b: { value: number }) => number;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "LawFirmPortfolio" });
  const [listed, setListed] = useState<Variant>("total");
  const [averaged, setAveraged] = useState<Variant>("total");

  const options = VARIANTS.map((variant) => ({
    value: variant,
    label: t(`variant.${variant}`),
  }));
  const description = (text: string) => (
    <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
      {text}
    </Typography>
  );

  return (
    <Box sx={cardRow}>
      <Paper
        component="section"
        variant="outlined"
        sx={{ ...card, pb: 1.25, minWidth: 0 }}
      >
        <CardHead
          title={t(`${kind}.byClaimType.title`)}
          control={
            <SegmentedControl
              value={listed}
              options={options}
              onChange={setListed}
              ariaLabel={t(`${kind}.byClaimType.shown`)}
            />
          }
        />
        {description(t(`${kind}.byClaimType.description`))}
        <Box sx={{ mt: 1.5 }}>
          <ClaimTypeBreakdown data={values[listed]} sort={sort} />
        </Box>
      </Paper>

      <Paper
        component="section"
        variant="outlined"
        sx={{ ...card, pb: 1.25, minWidth: 0 }}
      >
        <CardHead
          title={t(`${kind}.average.title`)}
          control={
            <SegmentedControl
              value={averaged}
              options={options}
              onChange={setAveraged}
              ariaLabel={t(`${kind}.average.shown`)}
            />
          }
        />
        {description(t(`${kind}.average.description`))}
        <Box sx={{ mt: 1.5 }}>
          <ClaimTypeAverages values={values[averaged]} />
        </Box>
      </Paper>
    </Box>
  );
};

/** The largest revenue first. */
const byValue = (a: { value: number }, b: { value: number }) =>
  b.value - a.value;

/** The largest profit or loss first. */
const bySize = (a: { value: number }, b: { value: number }) =>
  Math.abs(b.value) - Math.abs(a.value);

/**
 * What the firm makes of its cases: the profit it expects from each, how far
 * the active cases have come, and its revenue and its profit, in all, on
 * average and by the claim types of the cases.
 */
export const LawFirmPortfolio = () => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "LawFirmPortfolio" });
  const {
    revenueEstimated,
    revenueIncurred,
    marginEstimated,
    marginIncurred,
    marginProgressFlatAvg,
    marginProgressWeightedAvg,
    casesMarginProgress,
    revenue,
    margin,
    casesCostProgress,
    costProgressFlatAvg,
    costProgressWeightedAvg,
    lawFirmNetResultsPerCase,
    lawFirmNetResult,
    lawFirmNetResultMargin,
    casesWithoutFeeStructure,
    caseNameById,
  } = usePortfolio();

  const [progressType, setProgressType] = useState<ProgressType>("cost");
  const [isDistributionOpen, setDistributionOpen] = useState(false);
  const activeCaseCostProgress = casesCostProgress.filter(
    (c) => c.progress < 1,
  );
  const numberOfCompletedCases = casesCostProgress.filter(
    (costs) => costs.total > 0 && costs.incurred === costs.total,
  ).length;
  const noActiveCases = activeCaseCostProgress.length === 0;

  const activeCaseMarginProgress = casesMarginProgress.filter(
    (c) => c.progress < 1,
  );

  const lawFirmNetResultChartData: InteractiveBar[] = useMemo(() => {
    return lawFirmNetResultsPerCase
      .map(({ caseId, value }) => ({
        label: caseNameById[caseId] ?? caseId,
        value,
        href: `/cases/${caseId}`,
      }))
      .sort((a, b) => a.value - b.value);
  }, [lawFirmNetResultsPerCase, caseNameById]);

  const expectedProfit = lawFirmNetResult.total;
  // on the revenue of the cases whose profit is known
  const expectedProfitMargin = lawFirmNetResultMargin;

  const [marginExtremesVariant, setMarginExtremesVariant] =
    useState<Exclude<Variant, "total">>("incurred");
  const marginExtremes =
    marginExtremesVariant === "incurred" ? marginIncurred : marginEstimated;

  const earnedFill = lighten(theme.palette.primary.main, EARNED_LIGHTEN);
  const estimatedFill = lighten(theme.palette.primary.main, ESTIMATED_LIGHTEN);
  const revenueColors = {
    earned: {
      color: earnedFill,
      ink: theme.palette.getContrastText(earnedFill),
    },
    estimated: {
      color: estimatedFill,
      ink: theme.palette.getContrastText(estimatedFill),
    },
  };
  // a profit is a gain: what is estimated of it stands back behind what is
  // earned, and a see-through fill takes the ink of the page
  const marginColors = {
    earned: { color: gainFill(theme), ink: theme.palette.text.primary },
    estimated: {
      color: gainFill(theme, true),
      ink: theme.palette.text.primary,
    },
  };

  const progress = {
    cost: {
      active: activeCaseCostProgress,
      weighted: costProgressWeightedAvg,
      flat: costProgressFlatAvg,
      barColor: revenueColors.earned.color,
      barInk: revenueColors.earned.ink,
      histogramColor: undefined,
    },
    margin: {
      active: activeCaseMarginProgress,
      weighted: marginProgressWeightedAvg,
      flat: marginProgressFlatAvg,
      barColor: marginColors.earned.color,
      barInk: marginColors.earned.ink,
      histogramColor: marginColors.earned.color,
    },
  }[progressType];

  /** The client and case at an end of the range of the profits. */
  const extreme = (label: string, entry: PartyValueInCase, isRuled = false) => (
    <Box sx={isRuled ? { borderLeft: 1, borderColor: "divider", pl: 3.5 } : {}}>
      <Typography variant="caption" sx={figureLabel}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.125 }}>
        <Typography
          variant="h5"
          component="p"
          sx={{ ...figureValue, color: resultInk(theme, entry.value) }}
        >
          {currency.format(entry.value)}
        </Typography>
        {entry.value < 0 && (
          <TonalBadge tone={theme.palette.data.negative}>
            {t("loss")}
          </TonalBadge>
        )}
      </Box>
      <MuiLink
        component={Link}
        to={`/cases/${entry.caseId}/analytics/revenue`}
        underline="hover"
        sx={{ typography: "body2", fontWeight: 600 }}
      >
        {t("margin.stats.client", {
          partyName: entry.partyName,
          caseName: entry.caseName,
        })}
      </MuiLink>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2.25,
        userSelect: "none",
      }}
    >
      <SectionHeading id="netResult">{t("netResults.title")}</SectionHeading>

      {/* what the firm expects to be left with: in all, as a margin on its
          revenue, and from each client in each case */}
      <Paper component="section" variant="outlined" sx={card}>
        <SectionLabel component="h3">
          {t("netResults.perCase.title")}
        </SectionLabel>
        <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
          {t("netResults.perCase.description")}
        </Typography>
        <WithoutFeeStructure count={casesWithoutFeeStructure} />
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            columnGap: 3.5,
            rowGap: 1.75,
            mt: 1.75,
          }}
        >
          <div>
            <Typography variant="caption" sx={figureLabel}>
              {expectedProfit < 0
                ? t("netResults.perCase.loss")
                : t("netResults.perCase.profit")}
            </Typography>
            <Typography
              variant="h4"
              component="p"
              sx={{ ...figureValue, color: resultInk(theme, expectedProfit) }}
            >
              {currency.format(expectedProfit)}
            </Typography>
          </div>
          <Box sx={{ borderLeft: 1, borderColor: "divider", pl: 3.5 }}>
            <Typography variant="caption" sx={figureLabel}>
              {t("netResults.perCase.margin")}
            </Typography>
            <Typography variant="h4" component="p" sx={figureValue}>
              {/* without a revenue there is nothing to state a margin on */}
              {expectedProfitMargin === undefined
                ? NO_VALUE
                : percentage.format(expectedProfitMargin)}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("netResults.perCase.marginOf")}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: CASE_CHART_MIN_WIDTH, mt: 2.5 }}>
            <LinkedBarChart data={lawFirmNetResultChartData} />
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1,
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              {t("netResults.perCase.chart")}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <SectionHeading id="revenue" rule>
        {t("revenue.title")}
      </SectionHeading>

      {/* how far the active cases are, as one bar, and how the clients spread
          around it */}
      <Paper component="section" variant="outlined" sx={card}>
        <CardHead
          title={t("progress.title")}
          control={
            // with no active case there is nothing to measure either way
            !noActiveCases && (
              <SegmentedControl
                value={progressType}
                options={[
                  { value: "cost", label: t("progress.cost.label") },
                  { value: "margin", label: t("progress.margin.label") },
                ]}
                onChange={setProgressType}
                ariaLabel={t("progress.measuredBy")}
              />
            )
          }
        />
        {progressType === "margin" && (
          <WithoutFeeStructure count={casesWithoutFeeStructure} />
        )}
        {noActiveCases ? (
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            {t("progress.noActiveCases", { count: numberOfCompletedCases })}
          </Typography>
        ) : (
          <>
            <Box sx={{ mt: 1.75 }}>
              <ToggleProgressBar
                label={t(`progress.${progressType}.title`, {
                  count: progress.active.length,
                })}
                primaryLabel={t(`progress.${progressType}.weighted`)}
                secondaryLabel={t(`progress.${progressType}.flat`)}
                primaryValue={progress.weighted}
                secondaryValue={progress.flat}
                barColor={progress.barColor}
                barInk={progress.barInk}
                choiceLabel={t("progress.average")}
              />
            </Box>
            <Box sx={{ mt: 2.25, pt: 1, borderTop: 1, borderColor: "divider" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {t(`progress.${progressType}.distribution`, {
                    count: progress.active.length,
                  })}
                </Typography>
                <IconButton
                  size="small"
                  aria-expanded={isDistributionOpen}
                  aria-label={
                    isDistributionOpen
                      ? t("progress.hideDistribution")
                      : t("progress.showDistribution")
                  }
                  onClick={() => setDistributionOpen((isOpen) => !isOpen)}
                >
                  {isDistributionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={isDistributionOpen} timeout="auto">
                <Box sx={{ pt: 1.75 }}>
                  <HistogramChart
                    data={progress.active.map((c) => c.progress)}
                    label={`LawFirmPortfolio.progress.${progressType}.clients`}
                    tooltip={`LawFirmPortfolio.progress.${progressType}.inRange`}
                    backgroundColor={progress.histogramColor}
                    bins={PROGRESS_BINS}
                  />
                </Box>
              </Collapse>
            </Box>
          </>
        )}
      </Paper>

      <Box sx={cardRow}>
        <EarnedAndEstimatedCard
          title={t("revenue.allCases")}
          earned={revenueIncurred.total}
          estimated={revenueEstimated.total}
          colors={revenueColors}
        />
        <EarnedAndEstimatedCard
          title={t("revenue.perClient")}
          earned={revenueIncurred.avg}
          estimated={revenueEstimated.avg}
          colors={revenueColors}
        />
      </Box>

      <ByClaimType kind="revenue" values={revenue} sort={byValue} />

      <SectionHeading id="margin" rule>
        {t("margin.title")}
      </SectionHeading>
      <WithoutFeeStructure count={casesWithoutFeeStructure} />

      <Box sx={cardRow}>
        <EarnedAndEstimatedCard
          title={t("margin.allCases")}
          earned={marginIncurred.total}
          estimated={marginEstimated.total}
          colors={marginColors}
          isResult
        />
        <EarnedAndEstimatedCard
          title={t("margin.perClient")}
          earned={marginIncurred.avg}
          estimated={marginEstimated.avg}
          colors={marginColors}
          isResult
        />
      </Box>

      <ByClaimType kind="margin" values={margin} sort={bySize} />

      {/* the clients with the highest and with the lowest profit, each a way
          to the revenue of their case */}
      <Paper component="section" variant="outlined" sx={{ ...card, pb: 2.5 }}>
        <CardHead
          title={t("margin.stats.title")}
          control={
            <SegmentedControl
              value={marginExtremesVariant}
              options={[
                { value: "incurred", label: t("variant.incurred") },
                { value: "estimated", label: t("variant.estimated") },
              ]}
              onChange={setMarginExtremesVariant}
              ariaLabel={t("margin.stats.compared")}
            />
          }
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(300px, 100%), 1fr))",
            columnGap: 3.5,
            rowGap: 2.25,
            mt: 2,
          }}
        >
          {marginExtremes.max &&
            extreme(
              t(`margin.stats.${marginExtremesVariant}.max`),
              marginExtremes.max,
            )}
          {marginExtremes.min &&
            extreme(
              t(`margin.stats.${marginExtremesVariant}.min`),
              marginExtremes.min,
              true,
            )}
        </Box>
      </Paper>
    </Box>
  );
};
